import { useRef, useState, useEffect, useCallback } from "react";
import { api, API } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X, Loader2, ZoomIn, FileImage, Trash2, Printer, Check } from "lucide-react";

/**
 * Compact, multi-image spec uploader.
 *
 * Props:
 *  - recordId: string
 *  - images: array of {path, name, uploaded_at, uploaded_by, content_type}
 *  - canEdit: boolean — show upload / remove controls
 *  - onChange: (newImages) => void — fired after successful upload/delete (passes the API response record)
 *  - layout: "compact" (default — sidebar style) | "full"
 */
export const SpecImageUploader = ({ recordId, images = [], canEdit, onChange, layout = "compact" }) => {
  const [uploading, setUploading] = useState(false);
  const [thumbs, setThumbs] = useState({}); // index -> blob URL
  const [missing, setMissing] = useState({}); // index -> true when storage returned 410/404 (file unreachable)
  const [zoomIdx, setZoomIdx] = useState(null);
  const [selected, setSelected] = useState(new Set()); // multi-select indexes
  const inputRef = useRef(null);

  const loadThumb = useCallback(async (idx) => {
    if (thumbs[idx] || missing[idx]) return;
    try {
      const token = localStorage.getItem("wbt_token");
      const r = await fetch(`${API}/bath-records/${recordId}/spec-images/${idx}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Storage key re-scoped (post-redeploy) — backend now returns 410 Gone
      // with a placeholder PNG so we flag the image as "unreachable" and show
      // a graceful fallback rather than spinning forever.
      if (r.status === 410 || r.status === 404) {
        setMissing((prev) => ({ ...prev, [idx]: true }));
        return;
      }
      if (!r.ok) {
        setMissing((prev) => ({ ...prev, [idx]: true }));
        return;
      }
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      setThumbs((prev) => ({ ...prev, [idx]: url }));
    } catch {
      setMissing((prev) => ({ ...prev, [idx]: true }));
    }
  }, [recordId, thumbs, missing]);

  useEffect(() => {
    // Pre-fetch all thumbs (small images, only a few per record)
    images.forEach((_, i) => loadThumb(i));
    // Cleanup blob URLs when images list changes
    return () => {
      Object.values(thumbs).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, images.length]);

  // Drop selections that no longer exist
  useEffect(() => {
    setSelected((prev) => {
      const valid = new Set(images.map((_, i) => i));
      const next = new Set();
      prev.forEach((i) => valid.has(i) && next.add(i));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  const toggleSelect = (idx, e) => {
    e?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const printIndexes = async (indexes) => {
    if (!indexes || indexes.length === 0) return;
    // Make sure every thumb is loaded so the print window can embed them.
    const missing = indexes.filter((i) => !thumbs[i]);
    if (missing.length > 0) {
      await Promise.all(missing.map((i) => loadThumb(i)));
    }
    // Re-read state after loading. Use functional access to current thumbs.
    const urls = indexes.map((i) => thumbs[i]).filter(Boolean);
    if (urls.length === 0) {
      toast.error("Images not ready yet — try again in a moment");
      return;
    }
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) {
      toast.error("Allow pop-ups to print");
      return;
    }
    const ts = new Date().toLocaleString();
    const html = `<!doctype html>
<html><head><title>Flowline — Spec print (${urls.length})</title>
<style>
  @page { margin: 12mm; size: auto; }
  html, body { margin: 0; padding: 0; background: #fff; font-family: system-ui, sans-serif; color: #111; }
  .sheet {
    box-sizing: border-box;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
  }
  .sheet:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .sheet img { max-width: 100%; max-height: 88vh; object-fit: contain; display: block; }
  .cap {
    margin-top: 6mm;
    font: 10px ui-monospace, SFMono-Regular, Menlo, monospace;
    color: #6b7280;
    text-align: center;
  }
</style></head>
<body>
${urls
  .map(
    (u, i) =>
      `<div class="sheet"><img src="${u}" alt="Spec ${i + 1}" onload="window.__loaded=(window.__loaded||0)+1;if(window.__loaded===${urls.length})window.print();" onerror="window.__loaded=(window.__loaded||0)+1;if(window.__loaded===${urls.length})window.print();" /><div class="cap">Flowline · Spec ${i + 1} of ${urls.length} · ${ts}</div></div>`
  )
  .join("")}
<script>
  // Belt-and-braces: if any image never loads (e.g. blob URL revoked),
  // still trigger the print dialog after 5s so the popup doesn't hang.
  setTimeout(function(){ if(!window.__printed){ window.__printed=true; window.print(); } }, 5000);
</script>
</body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const printSelected = () => printIndexes(Array.from(selected).sort((a, b) => a - b));
  const printSingle = (idx) => printIndexes([idx]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use a JPEG, PNG or WebP image");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/bath-records/${recordId}/spec-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Spec uploaded");
      onChange && onChange(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onRemove = async (idx) => {
    if (!window.confirm("Remove this spec image?")) return;
    try {
      await api.delete(`/bath-records/${recordId}/spec-images/${idx}`);
      toast.success("Image removed");
      // Reload record by asking the parent to refresh
      onChange && onChange(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove image");
    }
  };

  const empty = !images || images.length === 0;
  const isCompact = layout === "compact";

  return (
    <>
      <div className="space-y-2">
        {/* Image grid */}
        {!empty && (
          <div className={`grid ${isCompact ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-3"} gap-2`}>
            {images.map((img, i) => {
              const isSelected = selected.has(i);
              return (
                <div
                  key={img.path}
                  className={`relative group block aspect-square bg-zinc-50 border rounded-md overflow-hidden transition-colors ${
                    isSelected ? "border-zinc-950 ring-2 ring-zinc-950/20" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`spec-thumb-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setZoomIdx(i)}
                    className="absolute inset-0 w-full h-full"
                    aria-label={`Open ${img.name || `Spec ${i + 1}`}`}
                  >
                    {thumbs[i] ? (
                      <img src={thumbs[i]} alt={img.name || "Spec"} className="w-full h-full object-cover" />
                    ) : missing[i] ? (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center bg-amber-50 text-amber-700 p-1 text-center"
                        data-testid={`spec-thumb-missing-${i}`}
                        title="File unreachable in storage — contact support to restore"
                      >
                        <FileImage className="h-4 w-4 mb-0.5" strokeWidth={1.5} />
                        <span className="text-[9px] font-bold uppercase tracking-wider leading-none">Unavailable</span>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                  </button>
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-[9px] text-white px-1 py-0.5 truncate font-mono pointer-events-none">
                    {img.name || `Spec ${i + 1}`}
                  </span>
                  {/* Select checkbox — hover-revealed, or always shown when selection active */}
                  <button
                    type="button"
                    onClick={(e) => toggleSelect(i, e)}
                    className={`absolute top-1 left-1 h-5 w-5 rounded border flex items-center justify-center transition-all ${
                      isSelected
                        ? "bg-zinc-950 border-zinc-950 text-white opacity-100"
                        : "bg-white/90 border-zinc-300 text-transparent opacity-0 group-hover:opacity-100"
                    } ${selected.size > 0 ? "opacity-100" : ""}`}
                    title={isSelected ? "Unselect" : "Select to print"}
                    data-testid={`spec-select-${i}`}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </button>
                  <ZoomIn className="absolute top-1 right-1 h-3 w-3 text-white drop-shadow opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </div>
              );
            })}
          </div>
        )}

        {/* Multi-select action bar */}
        {selected.size > 0 && (
          <div
            className="flex items-center gap-2 rounded-md border border-zinc-900 bg-zinc-950 text-white px-3 py-2 text-xs"
            data-testid="spec-print-bar"
          >
            <span className="font-semibold" data-testid="spec-selected-count">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-zinc-400 hover:text-white ml-auto"
              data-testid="spec-clear-selection"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <Button
              size="sm"
              onClick={printSelected}
              className="h-7 bg-white text-zinc-950 hover:bg-zinc-100 text-xs"
              data-testid="spec-print-selected"
            >
              <Printer className="h-3 w-3 mr-1" />
              Print {selected.size === 1 ? "image" : `${selected.size} images`}
            </Button>
          </div>
        )}

        {empty && (
          <div className="text-center py-4 px-2 border border-dashed border-zinc-200 rounded-md bg-zinc-50">
            <FileImage className="h-6 w-6 text-zinc-300 mx-auto mb-1" strokeWidth={1.5} />
            <p className="text-[11px] text-zinc-500">No spec uploaded yet</p>
            <p className="text-[10px] text-zinc-400">JPEG · PNG · WebP · max 6MB</p>
          </div>
        )}

        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onFile}
              data-testid="spec-image-input"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onPick}
              disabled={uploading}
              className="w-full border-zinc-200"
              data-testid="spec-image-upload-button"
            >
              {uploading ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…</>
              ) : (
                <><Upload className="h-3.5 w-3.5 mr-1.5" /> {empty ? "Upload spec" : "Add another"}</>
              )}
            </Button>
          </>
        )}
      </div>

      {/* Zoom modal */}
      {zoomIdx !== null && thumbs[zoomIdx] && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoomIdx(null)}
          data-testid="spec-image-zoom-overlay"
        >
          <div className="relative max-h-full max-w-full" onClick={(e) => e.stopPropagation()}>
            <img src={thumbs[zoomIdx]} alt="Spec full" className="max-h-[90vh] max-w-[90vw] object-contain" />
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                type="button"
                onClick={() => { printSingle(zoomIdx); setZoomIdx(null); }}
                className="bg-white/90 hover:bg-white text-zinc-900 rounded-md p-2"
                data-testid="spec-image-zoom-print"
                title="Print this image"
              >
                <Printer className="h-4 w-4" />
              </button>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => { onRemove(zoomIdx); setZoomIdx(null); }}
                  className="bg-rose-600/90 hover:bg-rose-700 text-white rounded-md p-2"
                  data-testid="spec-image-zoom-remove"
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setZoomIdx(null)}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-md p-2 text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="absolute bottom-2 left-2 text-white text-[11px] font-mono bg-black/40 backdrop-blur-sm rounded px-2 py-1">
              {images[zoomIdx]?.name} · {images[zoomIdx]?.uploaded_by}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
